import React, { useState, useEffect } from "react";
import { TextField, Button, Box } from "@mui/material";
import { useForm } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import Loader from "../../components/Loader";
import Toast from "../../utils/Toast";
import Swal from "sweetalert2";

import useProfile from "../../queries/profile/profile";
import useChangePassword from "../../mutation/changePassword/changePassword";
import {
  ListPageShell,
  ListPageCard,
  ListPageHeader,
} from "../../components/ListPage";

const ChangePasswordSchema = yup.object().shape({
  newPassword: yup
    .string()
    .min(8, "Password must contain at least 8 characters")
    .matches(
      /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z])/,
      "Password must contain at least 1 uppercase, lowercase character and number"
    )
    .required("Password is required"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("newPassword"), null], "Password does not match!")
    .required("Confirm Password is required"),
});

const Settings = () => {
  const {
    register,
    errors,
    handleSubmit,
    control,
    setValue,
    getValues,
  } = useForm({
    resolver: yupResolver(ChangePasswordSchema),
  });

  let [profileResponse, reset] = useProfile();
  let [passwordResponse, setData] = useChangePassword();

  const onSubmit = (values) => {
    setData({
      password: values.newPassword,
      confirmPassword: values.confirmPassword,
    });

    Swal.showLoading();
  };

  useEffect(() => {
    if (passwordResponse.data) {
      Swal.close();
      Toast.fire({
        icon: "success",
        title: "Password has been changed",
      });
      setValue("newPassword", "");
      setValue("confirmPassword", "");
    } else if (passwordResponse.error) {
      Swal.close();
      Toast.fire({
        icon: "error",
        title: "An error has occured. Please try again.",
      });
      setValue("newPassword", "");
      setValue("confirmPassword", "");
    }
  }, [passwordResponse]);

  const renderContent = () => {
    return (
      <div>
        <ListPageHeader title="Account Information" />
        {profileResponse.data && (
          <table>
            <tbody>
              <tr>
                <td style={{ padding: 10 }}>
                  <b>Name:</b>
                </td>
                <td style={{ padding: 10 }}>
                  {profileResponse.data.username}
                </td>
              </tr>
              <tr>
                <td style={{ padding: 10 }}>
                  <b>Email:</b>
                </td>
                <td style={{ padding: 10 }}>
                  {profileResponse.data.email}
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    );
  };

  const renderChangePassword = () => {
    return (
      <div>
        <ListPageHeader title="Change Password" />
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <TextField
            name="newPassword"
            margin="normal"
            fullWidth
            type="password"
            label="New Password"
            variant="outlined"
            inputRef={register}
            error={errors.newPassword != null}
            helperText={
              errors.newPassword != null ? errors.newPassword.message : null
            }
            disabled={passwordResponse.loading}
          />
          <TextField
            name="confirmPassword"
            margin="normal"
            fullWidth
            label="Re-enter Password"
            type="password"
            variant="outlined"
            inputRef={register}
            error={errors.confirmPassword != null}
            helperText={
              errors.confirmPassword != null
                ? errors.confirmPassword.message
                : null
            }
            disabled={passwordResponse.loading}
          />
          <Box sx={{ mt: 2.5, display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="submit"
              size="large"
              variant="contained"
              color="primary"
              disabled={passwordResponse.loading}
            >
              Submit
            </Button>
          </Box>
        </form>
      </div>
    );
  };

  return (
    <ListPageShell>
      <ListPageCard>
        {(profileResponse.loading || profileResponse.loading) && <Loader />}
        {!profileResponse.loading &&
          !profileResponse.loading &&
          renderContent()}
      </ListPageCard>
      <ListPageCard sx={{ mt: 2.5 }}>{renderChangePassword()}</ListPageCard>
    </ListPageShell>
  );
};

export default Settings;
